import { useEffect, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { restaurantService, Restaurant } from '@/services/restaurantService';

export function useRestaurants() {
  const {
    user,
    restaurants,
    currentRestaurant,
    setRestaurants,
    setCurrentRestaurant,
    switchRestaurant,
  } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);

  // Charger les restaurants de l'utilisateur
  const loadRestaurants = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const userRestaurants = await restaurantService.getUserRestaurants(user.id);
      setRestaurants(userRestaurants);

      // Si aucun restaurant sélectionné, sélectionner le premier ou celui sauvegardé
      if (userRestaurants.length > 0 && !currentRestaurant) {
        const savedId = restaurantService.getSelectedRestaurantId();
        const savedRestaurant = savedId
          ? userRestaurants.find((r) => r.id === savedId) ?? null
          : null;

        setCurrentRestaurant(savedRestaurant ?? userRestaurants[0] ?? null);

        // Sauvegarder si pas déjà fait
        if (!savedId && userRestaurants[0]) {
          restaurantService.setSelectedRestaurantId(userRestaurants[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur chargement restaurants:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, currentRestaurant, setRestaurants, setCurrentRestaurant]);

  // Charger automatiquement au montage et quand l'utilisateur change
  useEffect(() => {
    if (user?.id) {
      loadRestaurants();
    }
  }, [user?.id, loadRestaurants]);

  // Changer de restaurant
  const handleSwitchRestaurant = useCallback(
    (restaurantId: string) => {
      switchRestaurant(restaurantId);
      restaurantService.setSelectedRestaurantId(restaurantId);
      // Les composants réagiront automatiquement au changement via leurs useEffects
    },
    [switchRestaurant]
  );

  // Créer un nouveau restaurant
  const createRestaurant = useCallback(
    async (data: { nom: string; adresse?: string; telephone?: string; email?: string }) => {
      if (!user?.id) return null;

      const newRestaurant = await restaurantService.createRestaurant(user.id, data);

      if (newRestaurant) {
        // Rafraîchir la liste
        await loadRestaurants();

        // Sélectionner le nouveau restaurant
        handleSwitchRestaurant(newRestaurant.id);
      }

      return newRestaurant;
    },
    [user?.id, loadRestaurants, handleSwitchRestaurant]
  );

  return {
    restaurants,
    currentRestaurant,
    isLoading,
    hasMultipleRestaurants: restaurants.length > 1,
    switchRestaurant: handleSwitchRestaurant,
    createRestaurant,
    refreshRestaurants: loadRestaurants,
  };
}

export type { Restaurant };
